import { useEffect, useRef, useState } from 'react';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Button from '@v2/shared/components/Button';
import EmailVerificationBanner from '@v2/shared/components/EmailVerificationBanner';
import PreferencesTabs from '@v2/shared/components/PreferencesTabs';
import { FormCheckbox, FormInput } from '@v2/shared/components/Form';
import { Props } from './types';

/** The account dashboard, still legacy customer.php — the same entry the menu opens with. */
const ACCOUNT_URL = '/customer?xCmd=account';

/**
 * The three fixed names in acupdate's form contract. They are written here rather than
 * passed as props because they are not data: they are the endpoint's spelling, and
 * customer.php reads them literally — see the `xAcSection == 'E'` branch and
 * isCsrfTokenValid('account').
 */
const CSRF_FIELD = '_csrf_token';
const SECTION_FIELD = 'xAcSection';
const SECTION = 'E';

/** The break the "I need a break" boxes ask for. Read by acupdate as `xUnsubscribe`. */
const PAUSE_FIELD = 'xUnsubscribe';
const PAUSE_ONE_MONTH = '1M';
const PAUSE_TWO_WEEKS = '2W';

/**
 * The copy for each list, keyed by the name customer.php calls it.
 *
 * In the component rather than in props because it is copy, and because the server has
 * no better idea what these say than this file does — the legacy template hardcoded the
 * same four sentences behind the same four `{#if:fieldname:eq:...#}` branches. A list
 * the server sends that is not named here still renders, under its own id, rather than
 * disappearing.
 */
const LIST_COPY: Record<string, string> = {
  dailyNewsletter: 'Send me weekday personalized offers.',
  weeklyNewsletter: 'Send me weekend event sales.',
  renewalCampaign: 'Email me when my subscription is about to expire.',
  cartAbandonment: 'Email me when I leave magazines in my cart.',
};

/**
 * customer.php?xCmd=epshow — what we are allowed to email this customer about.
 *
 * The SMS half's mirror, and the reason this page moved: the two are one setting on two
 * pages, and until now only one of them was on the V2 account layout.
 *
 * The backend did not move with it. This is a plain form that posts to `acupdate` with
 * the field names the legacy form used, so every branch behind it — the email change and
 * its verification mail, the list subscribe/unsubscribe, the pause — runs exactly as
 * before. Nothing here is fetched; customer.php has already assembled all of it, and
 * EmailPreferencesPagePropsBuilder translates it on the way in.
 *
 * So this is deliberately not the SMS page's shape. That one talks to a storefront API
 * and redraws from what the server answers; this one submits and reloads, because that
 * is what the untouched backend does. The success and failure messages come back on the
 * next render rather than from a response.
 */
export default function EmailPreferences({
  formAction,
  csrfToken,
  emailPreferencesUrl,
  smsPreferencesUrl,
  email: initialEmail,
  emailFieldName,
  emailError,
  formError,
  saved,
  lists,
  pause: initialPause,
  resumeDate,
  unverifiedEmail,
  resendVerificationUrl,
}: Props) {
  const [email, setEmail] = useState(initialEmail);
  const [subscribed, setSubscribed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(lists.map((list) => [list.id, list.subscribed]))
  );
  const [pause, setPause] = useState<string | null>(initialPause);

  const formRef = useRef<HTMLFormElement>(null);
  const [submitAfterClearing, setSubmitAfterClearing] = useState(false);

  // "Unsubscribe from all promotional emails" is the same submission with every list
  // box cleared, which is what the legacy link did. It has to wait a render, because
  // the boxes are controlled and the form must post the cleared state, not the old one.
  useEffect(() => {
    if (!submitAfterClearing) {
      return;
    }

    setSubmitAfterClearing(false);
    formRef.current?.requestSubmit();
  }, [submitAfterClearing]);

  const toggleList = (id: string, checked: boolean) => {
    setSubscribed((current) => ({ ...current, [id]: checked }));
  };

  // The two breaks are alternatives, not a pair — the legacy form unchecked one when
  // the other was ticked. Clicking the ticked one clears it, so a customer can cancel
  // their break without picking the other length.
  const togglePause = (interval: string) => {
    setPause((current) => (current === interval ? null : interval));
  };

  const unsubscribeFromEverything = () => {
    setSubscribed({});
    setSubmitAfterClearing(true);
  };

  return (
    <div>
      {/* This page's trail, not the layout's — see SideMenu. */}
      <Breadcrumbs items={[{ label: 'Account', href: ACCOUNT_URL }, { label: 'Notifications' }]} />

      {/*
        The legacy account layout drew this above every page under it. This page is off
        that layout now and carries it itself, which matters here more than anywhere:
        acupdate clears the verified flag whenever the address changes, so a customer
        who edits it below is unverified the moment this page reloads.
      */}
      {unverifiedEmail && (
        <EmailVerificationBanner
          email={unverifiedEmail}
          resendUrl={resendVerificationUrl}
          className="max-w-2xl"
        />
      )}

      <h1 className="font-bold text-2xl md:text-3xl mb-3">Notifications</h1>

      <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-8 font-medium max-w-3xl">
        Choose how you would like to hear from us.
      </p>

      <PreferencesTabs
        current="email"
        emailPreferencesUrl={emailPreferencesUrl}
        smsPreferencesUrl={smsPreferencesUrl}
      />

      {saved && (
        <Alert color="success" className="mb-5 max-w-2xl">
          Email preferences successfully updated
        </Alert>
      )}

      {formError && (
        <Alert color="danger" className="mb-5 max-w-2xl">
          {formError}
        </Alert>
      )}

      {/* Not onSubmit-intercepted: this posts to acupdate the way the legacy form did. */}
      <form ref={formRef} method="post" action={formAction} className="max-w-2xl">
        <input type="hidden" name={CSRF_FIELD} value={csrfToken} />
        <input type="hidden" name={SECTION_FIELD} value={SECTION} />

        <FormInput
          id="emailaddress"
          type="email"
          name={emailFieldName}
          label="Email Address"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={emailError ?? undefined}
          autoComplete="email"
          required
        />

        <h2 className="font-bold text-lg md:text-xl mt-8 mb-4">Frequency Preferences</h2>

        {/*
          No category picker under the personalized offers. It was on the legacy page and
          is deliberately not on this one: the selections it wrote are used for nothing,
          and a customer who had made some simply stops being asked. acupdate still reads
          `sections[]` and the column still exists — this page just no longer speaks for
          them.
        */}
        {lists.map((list) => (
          <FormCheckbox
            key={list.id}
            id={list.fieldName}
            name={list.fieldName}
            value="Y"
            label={LIST_COPY[list.id] ?? list.id}
            checked={Boolean(subscribed[list.id])}
            onChange={(event) => toggleList(list.id, event.target.checked)}
          />
        ))}

        <h2 className="font-bold text-lg md:text-xl mt-8 mb-4">I Need A Break</h2>

        {resumeDate && (
          <Alert color="info" className="mb-5">
            We won&rsquo;t send you emails until {resumeDate}
          </Alert>
        )}

        <FormCheckbox
          id="unsubscribe1Month"
          name={PAUSE_FIELD}
          value={PAUSE_ONE_MONTH}
          label={<>Don&rsquo;t email me for a month.</>}
          checked={pause === PAUSE_ONE_MONTH}
          onChange={() => togglePause(PAUSE_ONE_MONTH)}
        />

        <FormCheckbox
          id="unsubscribe2Weeks"
          name={PAUSE_FIELD}
          value={PAUSE_TWO_WEEKS}
          label="Just give me 2 weeks off."
          checked={pause === PAUSE_TWO_WEEKS}
          onChange={() => togglePause(PAUSE_TWO_WEEKS)}
        />

        <h2 className="font-bold text-lg md:text-xl mt-8 mb-4">Unsubscribe</h2>

        <p className="text-slate-600 mb-2">
          To unsubscribe from all promotional emails,{' '}
          <button
            type="button"
            onClick={unsubscribeFromEverything}
            className="bg-transparent border-0 p-0 underline text-slate-600 hover:text-slate-900 cursor-pointer"
          >
            click here
          </button>
          .
        </p>

        <p className="text-sm text-slate-600">
          ** Transactional emails such as order confirmation and important notices will remain
          unaffected by any selections made on this page.
        </p>

        <div className="mt-8">
          <Button type="submit" color="primary">
            Update
          </Button>
        </div>
      </form>
    </div>
  );
}

EmailPreferences.layoutName = 'Layouts.Account';
